import React from "react"
import { graphql } from "gatsby"
import {
  useTranslation,
  Trans,
  Link,
} from "@zestia/gatsby-plugin-react-i18next"
import findImage from "@/utils/find-image"
import useHeadTranslation from "@/hooks/use-head-translation"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { gtmTracking } from "@/utils/gtm-tracking"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import SecondaryNav from "@/components/SecondaryNav"
import StepByStepList from "@/components/StepByStepList"
import PartnerTable from "@/components/PartnerTable"
import FullWidthCTABanner from "@/components/FullWidthCTABanner"

import SolutionIcon from "@/icons/solution.svg"
import SplitPane from "@/components/SplitPane"
import BenefitsList from "@/components/BenefitsList"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("solution.meta-title")}
      description={t("solution.meta-description")}
    />
  )
}

const SolutionHome = ({ data }) => {
  const { t } = useTranslation()

  const tableData = [
    {
      title: t("solution.table.data.commission.title"),
      bronze: t("solution.table.data.commission.bronze"),
      silver: t("solution.table.data.commission.silver"),
      gold: t("solution.table.data.commission.gold"),
    },
    {
      title: t("solution.table.data.criteria.title"),
      bronze: t("solution.table.data.criteria.bronze"),
      silver: t("solution.table.data.criteria.silver"),
      gold: t("solution.table.data.criteria.gold"),
    },
    {
      title: t("solution.table.data.resources.title"),
      bronze: true,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.promotion.title"),
      bronze: true,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.assets.title"),
      bronze: true,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.review.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.podcast.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.alignment.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
    {
      title: t("solution.table.data.strategy.title"),
      bronze: false,
      silver: false,
      gold: true,
    },
    {
      title: t("solution.table.data.support.title"),
      bronze: false,
      silver: false,
      gold: true,
    },
    {
      title: t("solution.table.data.check-ins.title"),
      bronze: false,
      silver: false,
      gold: true,
    },
    {
      title: t("solution.table.data.reports.title"),
      bronze: false,
      silver: false,
      gold: true,
    },
  ]

  return (
    <MainLayout>
      <SecondaryNav
        navTitle={t("partner-program.navigation.title")}
        navTitleLink="/partner-program/"
        items={[
          {
            name: t("partner-program.navigation.items.affiliate"),
            link: "/partner-program/affiliate",
          },
          {
            name: t("partner-program.navigation.items.solution"),
            link: "/partner-program/solution",
          },
          {
            name: t("partner-program.navigation.items.technology"),
            link: "/partner-program/technology",
          },
          {
            name: t("partner-program.navigation.items.smb"),
            link: "/partner-program/smb",
          },
          {
            name: t("partner-program.navigation.items.terms"),
            link: "/partner-program/affiliate/terms",
          },
        ]}
      />

      <section className="container-fluid partner-program__header partner-program__header pt-16 md:pt-24">
        <p className="mb-0 text-bold">{t("solution.header.kicker")}</p>

        <h1 className="h1--xl-condensed mt-2">{t("solution.header.title")}</h1>

        <div className="partner-program__header__buttons mt-6">
          <a
            href="https://dash.partnerstack.com/application?company=capsule&group=solutionprovidertriage"
            className="btn  btn--lg"
          >
            {t("solution.header.buttons.cta")}
          </a>

          <Link
            to="/partners"
            className="btn btn--tertiary btn--lg"
            onClick={() => gtmTracking("Link Click", "Partner Directory", "")()}
          >
            {t("solution.header.buttons.directory")}
          </Link>
        </div>

        <GatsbyImage
          className="partner-program__header__image mt-8 md:mt-24"
          image={getImage(findImage(data.allFile, "header-image"))}
          alt={t("solution.header.img-alt")}
        />
      </section>

      <section className="container-fluid row mt-20 md:mt-30">
        <h2 className="col-md-5">{t("solution.partnership.title")}</h2>

        <div className="mt-8 md:mt-0 col-md-6 col-md-offset-1">
          <p className="p--lg">{t("solution.partnership.description.one")}</p>
          <p className="mb-0 mt-6 p--lg">
            {t("solution.partnership.description.two")}
          </p>
        </div>
      </section>

      <StepByStepList
        className="mt-20 md:mt-30"
        steps={{
          stepOne: {
            heading: t("solution.threeSteps.stepOne.heading"),
            subHeading: t("solution.threeSteps.stepOne.subHeading"),
          },
          stepTwo: {
            heading: t("solution.threeSteps.stepTwo.heading"),
            subHeading: t("solution.threeSteps.stepTwo.subHeading"),
          },
          stepThree: {
            heading: t("solution.threeSteps.stepThree.heading"),
            subHeading: t("solution.threeSteps.stepThree.subHeading"),
          },
          stepFour: {
            heading: t("solution.threeSteps.stepFour.heading"),
            subHeading: t("solution.threeSteps.stepFour.subHeading"),
          },
        }}
      />
      <SplitPane
        className="mt-20 md:mt-30"
        assetAlignment="left"
        image={{
          src: findImage(data.allFile, "capsule-dev"),
          alt: t("solution.benefits.image-alt"),
        }}
        imageClass="shadow-xl"
        title={t("solution.benefits.title")}
        titleClass={"h2"}
      >
        <BenefitsList
          benefits={[
            {
              title: t("solution.benefits.content.one.title"),
              description: t("solution.benefits.content.one.description"),
            },
            {
              title: t("solution.benefits.content.two.title"),
              description: t("solution.benefits.content.two.description"),
            },
            {
              title: t("solution.benefits.content.three.title"),
              description: t("solution.benefits.content.three.description"),
            },
            {
              title: t("solution.benefits.content.four.title"),
              description: t("solution.benefits.content.four.description"),
            },
            {
              title: t("solution.benefits.content.five.title"),
              description: t("solution.benefits.content.five.description"),
            },
          ]}
        />
      </SplitPane>

      <PartnerTable
        className="mt-20 md:mt-30 container-fluid"
        title={t("solution.table.title")}
        icon={SolutionIcon}
        data={tableData}
      />

      <div className="container-fluid row mt-20 md:mt-30">
        <div className="col-md-5">
          <h2 className="mb-0 h3">{t("common.faq")}</h2>
        </div>

        {/* FAQs */}
        <div className="mt-8 md:mt-0 col-md-6 col-md-offset-1">
          <h3 className="mb-0 h5">{t("solution.faq.itemOne.title")}</h3>
          <p>{t("solution.faq.itemOne.body")}</p>

          <h3 className="mb-0 h5">{t("solution.faq.itemTwo.title")}</h3>
          <p>{t("solution.faq.itemTwo.body")}</p>

          <h3 className="mb-0 h5">{t("solution.faq.itemThree.title")}</h3>
          <p>{t("solution.faq.itemThree.body")}</p>

          <h3 className="mb-0 h5">{t("solution.faq.itemFour.title")}</h3>
          <p>
            <Trans
              i18nKey="solution.faq.itemFour.body"
              components={{
                Link: (
                  <Link
                    to="/integrations/"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>

          <h3 className="mb-0 h5">{t("solution.faq.itemFive.title")}</h3>
          <p>
            <Trans
              i18nKey="solution.faq.itemFive.body"
              components={{
                Link: (
                  <Link
                    to="/partner-program/affiliate/terms/#payments"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>

          <h3 className="mb-0 h5">{t("solution.faq.itemSix.title")}</h3>
          <p>
            <Trans
              i18nKey="solution.faq.itemSix.body"
              components={{
                Link: (
                  <Link
                    to="/partner-program/affiliate/terms/#referral-fees"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <FullWidthCTABanner
        text={t("solution.cta.title")}
        buttonLink="https://dash.partnerstack.com/application?company=capsule&group=solutionprovider"
        buttonText={t("solution.cta.button")}
        className="mt-20 md:mt-30"
      />
    </MainLayout>
  )
}

export default SolutionHome

export const query = graphql`
  query {
    ...Locales
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "solution" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
